import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { FeatureProvider } from "./context/FeatureContext";
import AdminFeatures from "./pages/admin/AdminFeatures";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { MarketProvider } from "@/context/MarketContext";
import { AlertsProvider } from "@/context/AlertsContext";
import { ReplayProvider } from "@/context/ReplayContext";
import { WorkspaceProvider } from "@/context/WorkspaceContext";
import { ScannerProvider } from "@/context/ScannerContext";
import { Toaster } from "@/components/ui/sonner";
import Terminal from "@/pages/Terminal";
import Overview from "@/pages/Overview";
import ChartPage from "@/pages/ChartPage";
import WatchlistPage from "@/pages/WatchlistPage";
import MarketsPage from "@/pages/MarketsPage";
import ScannerPage from "@/pages/ScannerPage";
import AlertsPage from "@/pages/AlertsPage";
import NewsPage from "@/pages/NewsPage";
import SettingsPage from "@/pages/SettingsPage";
import { JournalPage, ScoreLabPage, DemoTradePage } from "@/pages/SimplePages";
import AdminShell from "@/pages/admin/AdminShell";
import { AdminOverview, AdminUsers, AdminRisk, AdminScanner, AdminHealth, AdminAudit } from "@/pages/admin/AdminPages";
import Login from "@/pages/Login";
import Register from "@/pages/Register";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        Loading Fela R.I.O…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="h-screen w-screen flex items-center justify-center text-sm text-muted-foreground" data-testid="auth-checking">Checking session…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "superadmin") return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ReplayProvider>
        <MarketProvider>
          <AlertsProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route element={<Protected><FeatureProvider><WorkspaceProvider><ScannerProvider><Terminal /></ScannerProvider></WorkspaceProvider></FeatureProvider></Protected>}>
                <Route path="/dashboard" element={<Overview />} />
                <Route path="/chart" element={<ChartPage />} />
                <Route path="/watchlist" element={<WatchlistPage />} />
                <Route path="/markets" element={<MarketsPage />} />
                <Route path="/scanner" element={<ScannerPage />} />
                <Route path="/journal" element={<JournalPage />} />
                <Route path="/demo-trade" element={<DemoTradePage />} />
                <Route path="/score-lab" element={<ScoreLabPage />} />
                <Route path="/alerts" element={<AlertsPage />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
              </Route>
              <Route path="/admin" element={<AdminOnly><FeatureProvider><AdminShell /></FeatureProvider></AdminOnly>}>
                <Route index element={<AdminOverview />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="features" element={<AdminFeatures />} />
                <Route path="risk" element={<AdminRisk />} />
                <Route path="scanner" element={<AdminScanner />} />
                <Route path="health" element={<AdminHealth />} />
                <Route path="audit" element={<AdminAudit />} />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
          </AlertsProvider>
        </MarketProvider>
        </ReplayProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
